/**
 * Copy text to the clipboard from inside a Vuetify dialog, over plain HTTP.
 *
 * Two things make the obvious one-liner fail in this panel:
 *
 *  1. `navigator.clipboard` only exists in a secure context. The panel is
 *     served over plain HTTP on :8086, so it is simply undefined there and the
 *     copy button would do nothing with no error in the console.
 *  2. The legacy `execCommand` path normally copies from a focused textarea --
 *     but a v-dialog runs a focus trap that steals focus back before the copy
 *     fires, so the clipboard ends up empty. Selecting a Range instead works
 *     regardless of what is focused.
 *
 * Lifted from components/pre-verification/dialog/OfferList.vue, which learned
 * both of these the hard way; kept here so the next caller does not have to.
 *
 * Resolves true when the text actually reached the clipboard.
 */
export async function copyToClipboard(text) {
  if (!text) return false;

  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (err) {
    // fall through to the legacy path
  }

  let span = null;
  const selection = window.getSelection();
  const previousRange =
    selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
  try {
    span = document.createElement("span");
    span.textContent = text;
    span.style.position = "fixed";
    span.style.left = "-9999px";
    span.style.whiteSpace = "pre";
    document.body.appendChild(span);

    const range = document.createRange();
    range.selectNodeContents(span);
    selection.removeAllRanges();
    selection.addRange(range);

    return document.execCommand("copy");
  } catch (err) {
    return false;
  } finally {
    if (span) document.body.removeChild(span);
    // Put back whatever the user had selected before the copy.
    if (selection) {
      selection.removeAllRanges();
      if (previousRange) selection.addRange(previousRange);
    }
  }
}

export default copyToClipboard;
